import { midiToFrequency } from '@corocanto/scoring';
import * as Tone from 'tone';
import { audioContext } from './context';
import { CHANNELS, type Channel } from './mix';
import { type Click, countInDuration, type PlaybackPlan } from './plan';
import type { PianoBuffers } from './samples';

const VOICE_VELOCITY = 0.75;
const ACCOMPANIMENT_VELOCITY = 0.6;
const MASTER_GAIN = 0.6;
/** Delay between pressing play and the first sound, so scheduling never runs late. */
const START_DELAY = 0.1;

/** A recorded take to play along with the parts (listening back, reviewing a submission). */
export interface VoiceTrack {
  buffer: AudioBuffer;
  /** Timeline second at the first sample of the recording, latency already removed. */
  timelineStartSec: number;
}

export type EnginePhase = 'stopped' | 'countIn' | 'playing';

export interface EnginePosition {
  phase: EnginePhase;
  /** Seconds on the timeline (while playing). */
  timelineSec: number;
  /** While counting in: the beat of the count-in bar being clicked (1, 2, 3…), 0 before the first click. */
  countInBeat: number;
}

/**
 * Plays a playback plan with one sampled piano per channel (S, A, T, B and "Eşlik"), all on one
 * Tone.Transport. Notes are played by frequency from the choir's reference pitch. Independent of
 * React: the UI talks to it through the usePracticePlayer hook.
 */
export class PracticeEngine {
  private readonly samplers: Record<Channel, Tone.Sampler>;
  private readonly gains: Record<Channel, Tone.Gain>;
  private readonly preview: Tone.Sampler;
  private readonly voiceGain: Tone.Gain;
  private voice: Tone.Player | null = null;
  /** Count-in clicks are scheduled on the audio clock; a new synth per play lets stop() cancel them. */
  private click: Tone.Synth | null = null;
  /** The metronome during playback (rehearsal mode): scheduled on the transport, so it loops with it. */
  private metronome: Tone.Synth | null = null;
  private readonly master: Tone.Gain;
  private readonly meter: Tone.Meter;
  private plan: PlaybackPlan | null = null;
  private transportStartsAt = 0;
  private countInStartsAt = 0;
  private onEnded: (() => void) | null = null;

  constructor(buffers: PianoBuffers) {
    audioContext();
    const urls: Record<number, AudioBuffer> = Object.fromEntries(buffers);
    this.master = new Tone.Gain(MASTER_GAIN).toDestination();
    this.meter = new Tone.Meter({ normalRange: true, smoothing: 0.6 });
    this.master.connect(this.meter);
    this.gains = {} as Record<Channel, Tone.Gain>;
    this.samplers = {} as Record<Channel, Tone.Sampler>;
    for (const ch of CHANNELS) {
      this.gains[ch] = new Tone.Gain(0).connect(this.master);
      this.samplers[ch] = new Tone.Sampler({ urls, release: 0.8 }).connect(this.gains[ch]);
    }
    this.preview = new Tone.Sampler({ urls, release: 0.8 }).connect(this.master);
    this.voiceGain = new Tone.Gain(1).connect(this.master);
  }

  /** Level of the recorded voice (0 mutes it). */
  setVoiceGain(gain: number): void {
    this.voiceGain.gain.rampTo(gain, 0.05);
  }

  setGains(gains: Record<Channel, number>): void {
    for (const ch of CHANNELS) this.gains[ch].gain.rampTo(gains[ch], 0.05);
  }

  /** Current gain per channel, as set (for display and tests). */
  gainValues(): Record<Channel, number> {
    const out = {} as Record<Channel, number>;
    for (const ch of CHANNELS) out[ch] = Math.round(this.gains[ch].gain.value * 1000) / 1000;
    return out;
  }

  /** Output level, 0 to 1. */
  level(): number {
    const v = this.meter.getValue();
    return typeof v === 'number' ? v : Math.max(...v);
  }

  setLoop(loop: boolean): void {
    const transport = Tone.getTransport();
    if (!this.plan) return;
    transport.loop = loop;
  }

  /** Start playing; returns the AudioContext time at which the range (after the count-in) starts. */
  play(
    plan: PlaybackPlan,
    options: {
      loop: boolean;
      referenceA4: number;
      onEnded: () => void;
      voice?: VoiceTrack;
      /** Semitones up (+) or down (−) for every note (rehearsal mode). */
      transpose?: number;
      /** Metronome clicks during playback (rehearsal mode). */
      metronome?: readonly Click[] | null;
    },
  ): number {
    this.stop();
    const transport = Tone.getTransport();
    this.plan = plan;
    this.onEnded = options.onEnded;
    const length = plan.rangeEndSec - plan.rangeStartSec;

    for (const note of plan.notes) {
      const sampler = this.samplers[note.channel];
      const frequency = midiToFrequency(note.midi + (options.transpose ?? 0), options.referenceA4);
      const velocity = note.channel === 'acc' ? ACCOMPANIMENT_VELOCITY : VOICE_VELOCITY;
      transport.schedule((time) => sampler.triggerAttackRelease(frequency, note.duration, time, velocity), note.time);
    }
    if (options.metronome && options.metronome.length > 0) {
      const metronome = this.newClickSynth(-10);
      this.metronome = metronome;
      for (const c of options.metronome) {
        transport.schedule((time) => metronome.triggerAttackRelease(c.accent ? 1760 : 1320, 0.03, time), c.time);
      }
    }
    transport.loopStart = 0;
    transport.loopEnd = length;
    transport.loop = options.loop;
    transport.schedule((time) => {
      if (transport.loop) return;
      Tone.getDraw().schedule(() => {
        this.stop();
        this.onEnded?.();
      }, time);
    }, Math.max(0, length - 0.001));

    const now = Tone.now();
    this.countInStartsAt = now + START_DELAY;
    if (plan.countIn) {
      const click = this.newClickSynth(-6);
      this.click = click;
      for (let i = 0; i < plan.countIn.clicks; i++) {
        const accent = i % plan.countIn.perBar === 0;
        click.triggerAttackRelease(accent ? 1760 : 1320, 0.03, this.countInStartsAt + i * plan.countIn.interval);
      }
    }
    this.transportStartsAt = this.countInStartsAt + countInDuration(plan.countIn);
    transport.start(this.transportStartsAt, plan.offsetSec);

    // The recording starts where its timeline position meets the transport (no looping).
    if (options.voice) {
      const player = new Tone.Player(options.voice.buffer).connect(this.voiceGain);
      const into = plan.rangeStartSec + plan.offsetSec - options.voice.timelineStartSec;
      if (into >= 0) {
        if (into < options.voice.buffer.duration) player.start(this.transportStartsAt, into);
      } else {
        player.start(this.transportStartsAt - into, 0);
      }
      this.voice = player;
    }
    return this.transportStartsAt;
  }

  stop(): void {
    const transport = Tone.getTransport();
    transport.stop();
    transport.cancel(0);
    transport.loop = false;
    for (const ch of CHANNELS) this.samplers[ch].releaseAll();
    this.click?.dispose();
    this.click = null;
    this.metronome?.dispose();
    this.metronome = null;
    this.voice?.dispose();
    this.voice = null;
    this.plan = null;
  }

  position(): EnginePosition {
    const plan = this.plan;
    if (!plan) return { phase: 'stopped', timelineSec: 0, countInBeat: 0 };
    const now = Tone.immediate();
    if (now < this.transportStartsAt) {
      const countIn = plan.countIn;
      const k = countIn ? Math.floor((now - this.countInStartsAt) / countIn.interval) : -1;
      return {
        phase: 'countIn',
        timelineSec: plan.rangeStartSec + plan.offsetSec,
        countInBeat: countIn && k >= 0 ? (k % countIn.perBar) + 1 : 0,
      };
    }
    const sec = Tone.getTransport().getSecondsAtTime(now);
    return { phase: 'playing', timelineSec: plan.rangeStartSec + sec, countInBeat: 0 };
  }

  /** Play one note on its own (e.g. "play my first note"), regardless of mode and mixer. */
  playNote(midi: number, durationSec: number, referenceA4: number): void {
    this.playChord([midi], durationSec, referenceA4);
  }

  /** Play notes together on their own (starting notes, the opening chord), regardless of the mixer. */
  playChord(midis: readonly number[], durationSec: number, referenceA4: number): void {
    const at = Tone.now() + 0.02;
    for (const midi of midis) this.preview.triggerAttackRelease(midiToFrequency(midi, referenceA4), durationSec, at, VOICE_VELOCITY);
  }

  private newClickSynth(volume: number): Tone.Synth {
    return new Tone.Synth({
      oscillator: { type: 'triangle' },
      envelope: { attack: 0.001, decay: 0.05, sustain: 0, release: 0.02 },
      volume,
    }).connect(this.master);
  }

  dispose(): void {
    this.stop();
    for (const ch of CHANNELS) {
      this.samplers[ch].dispose();
      this.gains[ch].dispose();
    }
    this.preview.dispose();
    this.voiceGain.dispose();
    this.meter.dispose();
    this.master.dispose();
  }
}
